import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo, type ReactNode } from "react";
import styled from "styled-components";

import { PlanogramItem } from "@CommonTypes/merchflow/pog/item";
import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import { useUser } from "src/atoms/user";
import {
  labelOptionAtom,
  planogramHoveredContainerAtom,
  planogramHoveredItemAtom,
  planogramHoveredPegboardPlacementAtom,
  planogramHoveredShelfAtom,
  planogramImagesModeAtom,
  planogramKonvaRendererAtom,
  planogramSelectedItemsAtom,
} from "src/components/PlanogramExtensions/store/atoms";
import {
  createPlanogramAssetProvider,
  usePlanogramRendererLabels,
} from "../../common/konvaRendererAdapter";
import {
  KonvaPlanogram,
  emptyPlanogramSelection,
  type OverlayChannelInput,
  type PlanogramEntityRef,
  type PlanogramOverlayBadgeRequest,
  type PlanogramProductLabel,
  type PlanogramSelection,
  type PlanogramWorldOverlay,
} from "../KonvaPlanogram";
import { konvaViewerPolicy } from "./konvaViewerPolicy";
import { useShowDividers } from "src/components/Planogram/common/shelfDividersView";
import { usePlanogramPanel } from "../Panel/store/hooks";
import { isPegboardPlacementSelection } from "../../common/planogramSelection";

const ViewerRoot = styled.div`
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
`;

interface Props {
  centerOnFit?: boolean;
  onInspectProduct?: (pog: Pog | null) => void;
  onOverlayBadgeActivate?: (request: PlanogramOverlayBadgeRequest) => void;
  overlayBadges?: ReadonlyMap<string, string>;
  overlayChannels?: readonly OverlayChannelInput[];
  worldOverlay?: PlanogramWorldOverlay;
  pog: Pog;
  scaleY: number;
  initialOffsetX?: number;
  scrollToEntityId?: string | null;
  /** The world overlay draws the product labels itself; the canvas skips its own. */
  hideProductLabels?: boolean;
}

export const KonvaViewerPlanogram = ({
  centerOnFit,
  onInspectProduct,
  onOverlayBadgeActivate,
  overlayBadges,
  overlayChannels,
  worldOverlay,
  pog,
  scaleY,
  initialOffsetX = 0,
  scrollToEntityId,
  hideProductLabels = false,
}: Props) => {
  const { user } = useUser();
  const imagesMode = useAtomValue(planogramImagesModeAtom);
  const { showDividers } = useShowDividers(pog);
  const labelOption: PlanogramProductLabel = useAtomValue(labelOptionAtom);
  const setHoveredItem = useSetAtom(planogramHoveredItemAtom);
  const setHoveredPegboardPlacement = useSetAtom(planogramHoveredPegboardPlacementAtom);
  const setHoveredShelf = useSetAtom(planogramHoveredShelfAtom);
  const setHoveredContainer = useSetAtom(planogramHoveredContainerAtom);
  const labels = usePlanogramRendererLabels();
  const setSelectedItems = useSetAtom(planogramSelectedItemsAtom);
  const { setTab } = usePlanogramPanel();
  const sharedSelectedItems = useAtomValue(planogramSelectedItemsAtom);
  const selection = useMemo<PlanogramSelection>(() => {
    const products = sharedSelectedItems.filter(
      (item) => item instanceof PlanogramItem && pog.getItem(item.uniqueId) === item,
    );
    const placements = sharedSelectedItems
      .filter(isPegboardPlacementSelection)
      .filter((item) => pog.planogram.getPegboardPlacement(item.uniqueId) === item);
    const ids = (products.length ? products : placements).map((item) => item.uniqueId);
    return ids.length
      ? {
          kind: products.length ? "product" : "placement",
          ids,
          anchorId: ids[0] ?? null,
          focusedId: ids.at(-1) ?? null,
        }
      : emptyPlanogramSelection;
  }, [pog, sharedSelectedItems]);
  useEffect(
    () => () => {
      setHoveredItem(null);
      setHoveredPegboardPlacement(null);
      setHoveredShelf(null);
      setHoveredContainer(null);
    },
    [pog, setHoveredItem, setHoveredPegboardPlacement, setHoveredShelf, setHoveredContainer],
  );

  const handleSelectionChange = (selection: PlanogramSelection) => {
    const selected =
      selection.kind === "product"
        ? selection.ids.flatMap((id) => {
            const item = pog.getItem(id);
            return item instanceof PlanogramItem ? [item] : [];
          })
        : selection.kind === "placement"
          ? selection.ids.flatMap((id) => {
              const item = pog.planogram.getPegboardPlacement(id);
              return item ? [item] : [];
            })
          : [];
    setSelectedItems(selected);
    if (selected.length) setTab("PRODUCT_DETAILS", true);
    onInspectProduct?.(selected.length ? pog : null);
  };

  const baseScale = Number.isFinite(scaleY) && scaleY > 0 ? scaleY : 0.01;
  const minScale = baseScale;
  const merchantId = user?.merchantId ?? null;
  const assetProvider = useMemo(
    () => (imagesMode ? createPlanogramAssetProvider(merchantId) : undefined),
    [imagesMode, merchantId],
  );

  const clearHover = () => {
    setHoveredItem(null);
    setHoveredPegboardPlacement(null);
    setHoveredShelf(null);
    setHoveredContainer(null);
  };

  const handleHoverChange = (entity: PlanogramEntityRef | null) => {
    if (entity?.kind === "product") {
      const item = pog.getItem(entity.id);
      clearHover();
      setHoveredItem(item instanceof PlanogramItem ? item : null);
      return;
    }

    if (entity?.kind === "placement") {
      clearHover();
      setHoveredPegboardPlacement(pog.planogram.getPegboardPlacement(entity.id) ?? null);
      return;
    }

    if (entity?.kind === "shelf") {
      clearHover();
      setHoveredShelf(pog.getShelf(entity.id) ?? null);
      return;
    }

    if (entity?.kind === "container") {
      clearHover();
      setHoveredContainer(
        pog.containers.find((candidate) => candidate.uniqueId === entity.id) ?? null,
      );
      return;
    }

    clearHover();
  };

  return (
    <ViewerRoot data-testid="konva-viewer-planogram">
      <KonvaPlanogram
        centerOnFit={centerOnFit}
        assetProvider={assetProvider}
        labels={labels}
        onHoverChange={handleHoverChange}
        onSelectionChange={handleSelectionChange}
        onActivate={(entity, activatedSelection) => {
          if ((entity.kind === "product" || entity.kind === "placement") && activatedSelection)
            handleSelectionChange(activatedSelection);
        }}
        onOverlayBadgeActivate={onOverlayBadgeActivate}
        overlayBadges={overlayBadges}
        overlayChannels={overlayChannels}
        worldOverlay={worldOverlay}
        policy={konvaViewerPolicy}
        pog={pog}
        selection={selection}
        scrollToEntityId={scrollToEntityId}
        viewportConfig={{
          // A whole run fitted to a small canvas needs more than 3x to read a label.
          bounds: { maxScale: baseScale * 8, minScale },
          defaultState: { scale: baseScale, offsetX: initialOffsetX },
        }}
        view={{
          productDisplayMode: imagesMode ? "image" : "label",
          outlineFacings: true,
          showDividers,
          productLabel: hideProductLabels ? "none" : labelOption,
          scale: baseScale,
        }}
      />
    </ViewerRoot>
  );
};

interface GateProps extends Props {
  children: ReactNode;
}

export const KonvaViewerPlanogramGate = ({ children, ...props }: GateProps) => {
  const konvaRenderer = useAtomValue(planogramKonvaRendererAtom);

  return konvaRenderer ? <KonvaViewerPlanogram {...props} /> : <>{children}</>;
};
